import { Button } from '@zoopla-eng/zds/button';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { SearchPageType, SearchSource } from '@zoopla/listing-search-context';
import {
    FormValues,
    getDuration,
    getFormValuesFromQuery,
    getQ,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import {
    BedsMax,
    BedsMin,
    PriceMax,
    PriceMin,
    SelectBeds,
    SelectPriceForSale,
    SelectPriceToRent
} from '@zoopla/listing-search/filters/options';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import {
    PropertySubType,
    PropertyType,
    PropertyTypeCheckboxes
} from '@zoopla/listing-search/property-type';
import { PriceFrequency } from '@zoopla/redux-reducer-filters';
import { triggerTracking } from '@zoopla/tracking';
import { NextRouter, useRouter } from 'next/router';
import { FC, MouseEvent, useEffect, useState } from 'react';
import { formatLocation } from '../utils/formatLocation';
import { formatSearchPath } from '../utils/formatSearchPath';
import { getQueryParams } from '../utils/getQueryParams';
import {
    filterDividerStyle,
    filtersLabelStyle,
    filterWrapperStyle,
    locationFilterWrapperStyle,
    modalContentWrapperStyle,
    stickyWrapperUpdateButtonStyle,
    travelWrapperStyle
} from './SearchInitiationMobileModal.styles.css';
import { Duration, SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectPropertyType } from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';
import { SelectTransportMode, TransportType } from './SelectTransportMode';

type Tab = SearchPageType | 'house-prices';

const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

export type SearchInitiationMobileModalProps = {
    showRadius?: boolean;
    updateResultsText: string;
    headlineText?: string;
    headerTextOverride?: string;
    close: () => void;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
};

const getIdentifier = (query: NextRouter['query']) =>
    formatLocation(getSearchPath(query));
const getLocation = (query: NextRouter['query']) =>
    getQ(query) || formatSearchPath(getSearchPath(query));

export const SearchInitiationMobileModal: FC<
    React.PropsWithChildren<SearchInitiationMobileModalProps>
> = ({
    showRadius = true,
    close,
    updateResultsText,
    searchType,
    eventCategory,
    searchSource
}) => {
    const { query, push, pathname, isReady } = useRouter();
    const [resolvedQuery, updateResolvedQuery] = useState(query);
    const [formValue, updateFormValue] = useState<FormValues>(
        getFormValuesFromQuery(query)
    );
    const [locationText, setLocationText] = useState(getLocation(query));
    const [identifier, setIdentifier] = useState(getIdentifier(query));
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    useEffect(() => {
        if (isReady) {
            updateResolvedQuery(query);
            updateFormValue((o) => ({
                ...o,
                ...getFormValuesFromQuery(query)
            }));
            setLocationText(getLocation(query));
        }
    }, [query, isReady]);

    const isTravelTimeSearch = Boolean(
        getDuration(resolvedQuery) && getTransportType(resolvedQuery)
    );
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';
    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        if (isNewHomesDevelopersSearch) {
            await push({
                pathname: identifier
                    ? `/new-homes/developers/${identifier}/`
                    : `/new-homes/developers/`,
                query: {
                    ...(formValue.beds_max && { beds_max: formValue.beds_max }),
                    ...(formValue.beds_min && { beds_min: formValue.beds_min }),
                    ...(formValue.price_min && {
                        price_min: formValue.price_min
                    }),
                    ...(formValue.price_max && {
                        price_max: formValue.price_max
                    }),
                    property_type: formValue.property_type,
                    q: formValue.q || locationText
                }
            });

            return;
        }

        const queryParams = getQueryParams(searchType);

        await push({
            pathname: '/search/',
            query: {
                ...queryParams,
                ...(formValue.beds_max && { beds_max: formValue.beds_max }),
                ...(formValue.beds_min && { beds_min: formValue.beds_min }),
                ...(formValue.price_min && {
                    price_min: formValue.price_min
                }),
                ...(formValue.price_max && {
                    price_max: formValue.price_max
                }),
                ...(formValue.property_sub_type && {
                    property_sub_type: formValue.property_sub_type
                }),
                ...(formValue.radius && { radius: formValue.radius }),
                ...(formValue.duration && { duration: formValue.duration }),
                ...(formValue.transport_type && {
                    transport_type: formValue.transport_type
                }),
                property_type: formValue.property_type,
                q: formValue.q || locationText,
                view_type: viewType
            }
        });
    };

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <div className={modalContentWrapperStyle}>
            <div className={locationFilterWrapperStyle}>
                <LocationSearchInput
                    id="filters-location-mobile"
                    onLocationInputKeyDown={handleSearchProperties}
                    onlyDisplayRemoveIconWhenActive={false}
                    value={locationText as string}
                    showError={showError}
                    onLocationSelect={({ identifier, q }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: q
                        }));
                        setIdentifier(identifier);
                        setLocationText(q);
                    }}
                    onClearText={() => setLocationText('')}
                    handleInputTextChange={(value) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: ''
                        }));
                        setIdentifier(value);
                        setLocationText(value);
                    }}
                />
            </div>

            <div className={filterDividerStyle} />

            {isTravelTimeSearch ? (
                <div className={filterWrapperStyle}>
                    <p className={`${filtersLabelStyle} ${fontGroups.ladle}`}>
                        Travel time
                    </p>
                    <div className={travelWrapperStyle}>
                        <SelectMaxTravelTime
                            value={formValue.duration as Duration}
                            onChange={(duration) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    duration: duration
                                }));
                            }}
                        />
                        <SelectTransportMode
                            value={formValue.transport_type as TransportType}
                            onChange={(transportType) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    transport_type: transportType
                                }));
                            }}
                        />
                    </div>
                </div>
            ) : showRadius ? (
                <SelectRadius
                    value={formValue.radius as Radius}
                    onChange={(radius) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            radius: radius
                        }));
                    }}
                />
            ) : null}

            <div className={filterDividerStyle} />

            {isForSaleSearch ? (
                <SelectPriceForSale
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax
                    }}
                    onChange={({ priceMin, priceMax }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax
                        }));
                    }}
                />
            ) : (
                <SelectPriceToRent
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax,
                        priceFrequency: (formValue.price_frequency ||
                            'per_month') as PriceFrequency
                    }}
                    onChange={({ priceMin, priceMax, priceFrequency }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax,
                            price_frequency: priceFrequency
                        }));
                    }}
                />
            )}

            <div className={filterDividerStyle} />

            <SelectBeds
                value={{
                    bedsMin: formValue.beds_min as BedsMin,
                    bedsMax: formValue.beds_max as BedsMax
                }}
                onChange={({ bedsMin, bedsMax }) => {
                    updateFormValue((oldFormValue) => ({
                        ...oldFormValue,
                        beds_min: bedsMin,
                        beds_max: bedsMax
                    }));
                }}
            />

            <div className={filterDividerStyle} />
            <label className={`${filtersLabelStyle} ${fontGroups.ladle}`}>
                Property type
            </label>
            {isNewHomesDevelopersSearch ? (
                <SelectPropertyType
                    value={formValue.property_type as PropertyType}
                    onChange={(propertyType) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_type: propertyType
                        }));
                    }}
                />
            ) : (
                <PropertyTypeCheckboxes
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertySubType, propertyType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_sub_type: propertySubType,
                            property_type: propertyType
                        }));
                    }}
                />
            )}
            <div className={stickyWrapperUpdateButtonStyle}>
                <Button
                    category="primary"
                    fw
                    onClick={handlePressUpdate}
                    data-testid="update-results-button"
                    title="Update"
                >
                    {updateResultsText}
                </Button>
            </div>
        </div>
    );
};
